import React from "react";
import ReactDOMClient from "react-dom/client";
import { Provider } from "react-redux";

import {
    ErrorBoundary,
    ErrorBoundaryFallbackComponent,
} from "@utils/errorBoundary";
import { parseElementForCommentedContent } from "@utils/ssr";

import { TStore } from "@redux/store.types";

const MFGraniteProvider = React.lazy(() =>
    import("@redux/modules/value/translations/translations.provider").then(
        module => ({
            default: module.GraniteProvider,
        }),
    ),
);

const filterByComponentTypeIfPresent = (
    componentType: string,
    filteredType?: string,
) => {
    return !!(
        !filteredType ||
        (filteredType && filteredType === componentType)
    );
};

const componentIsAlreadyHydrated = (element: HTMLElement) => {
    let isHydrated = false;
    for (let _prop in element) {
        if (_prop.indexOf("_react") > -1) {
            isHydrated = true;
        }
    }
    return !isHydrated;
};

const SSR = (store: TStore, componentType?: string) => {
    performance.mark("Hubo: Hydrator.ssr");

    const hydrationOrder: Array<string> = [
        "ECOMMERCE",
        "PRODUCT_PAGE",
        "IMAGE_VIEWER",
        "CART_BUTTON",
        "PRODUCT_SLIDER",
        "PRODUCT_LIST",
        "PRODUCT_TILE",
        "HEADER_BUTTON",
        "ACCOUNT_BUTTON",
        "WISHLIST_HEADER_BUTTON",
        "FLYOUT_BUTTON",
        "CATEGORIES_SLIDER",
        "IMAGE_VIEWER_FULL_WIDTH",
        "SAFETY_INFO",
        "LANGUAGE_SELECTOR",
        "DEBUG_NOTIFICATION",
        "PRODUCT_COMPARISON",
        "PRODUCT_COMPARISON_BAR",
        "SEARCH_BAR",
        "PRODUCT_BUNDLES",
        "STORE_LOCATOR_MOBILE",
        "CALL_TO_ACTION",
        "STORE_SEARCH",
        "STORE_DIRECTIONS",
        "NEWSLETTER",
        "LOGIN_FORM",
        "REGISTER_FORM",
        "FORGOT_PASSWORD_FORM",
        "RESET_PASSWORD_FORM",
        "REGISTER_CHECKOUT_FORM",
        "FRANCHISE_REQUEST",
        "CONTACT_CUSTOMER_SERVICE",
        "CREATE_GIFT_CARD",
        "BUSINESS_GIFT_CARD_REQUEST",
        "GIFT_CARD_REQUEST_BALANCE",
        "BONUS_CARD_REQUEST",
        "BONUS_CARD_REQUEST_BALANCE",
        "CUSTOMER_LOAD",
        "QUIZZI_APPOINTMENT",
    ].filter(cmpType => filterByComponentTypeIfPresent(cmpType, componentType));

    const storeRef = store;

    hydrationOrder
        // @ts-ignore
        .reduce(function (promise, componentKey) {
            return promise.then(function () {
                const ssrComponents = Array.from(
                    document.querySelectorAll<HTMLElement>(
                        `[data-ssr=${componentKey}]`,
                    ),
                )
                    .filter(c => c.closest(".js-modal") === null)
                    .filter(componentIsAlreadyHydrated);

                return Promise.all([
                    delay(0),
                    doSsrAsync(ssrComponents, storeRef),
                ]);
            });
        }, Promise.resolve())
        // @ts-ignore
        .then(() => {
            const images = document.querySelectorAll<HTMLImageElement>(
                'img[loading="lazy"]',
            );
            images.forEach(img => {
                if (img.dataset.src) {
                    img.src = img.dataset.src;
                }
            });

            performance.measure(
                "Hubo: Duration Hydrator.ssr",
                "Hubo: Hydrator.ssr",
            );
        });
};

const SSRModal = (store: TStore, element: HTMLElement) => {
    const ssrComponents = Array.from(
        element.querySelectorAll<HTMLElement>("[data-ssr]"),
    );
    const storeRef = store;
    doSsr(ssrComponents, storeRef);
};

const doSsr = async (elements: Array<HTMLElement>, store: TStore) => {
    if (elements) {
        const componentMap = window.hydrator.getComponentMap("SSR");
        for (const component of elements) {
            const componentName = component.dataset["ssr"] || "unknown";

            let Component = componentMap[componentName];

            if (!Component) {
                console.warn("SSR::MISSING COMPONENT - " + componentName);
                const { componentMap: FullMapping } = await import(
                    "../../../../full-lib/SSRComponents"
                );
                Component = FullMapping[componentName];
            }

            if (!Component) {
                console.log("Component nowhere to be found - ", componentName);
                return;
            }

            performance.mark("Hubo: Hydrator.ssr." + componentName);
            const uuid = component.dataset.ssrComponentUuid;

            const componentPropsElement = document.querySelector<HTMLElement>(
                `script[data-ssr-data-uuid='${uuid}']`,
            );
            const componentProps = parseElementForCommentedContent(
                componentPropsElement,
            );

            ReactDOMClient.hydrateRoot(
                component,
                <ErrorBoundary
                    FallbackComponent={ErrorBoundaryFallbackComponent}
                >
                    <React.Suspense>
                        <Provider store={store}>
                            <MFGraniteProvider>
                                <Component {...componentProps} />
                            </MFGraniteProvider>
                        </Provider>
                    </React.Suspense>
                </ErrorBoundary>,
            );
            performance.measure(
                "Hubo: Hydrator.ssr." + componentName,
                "Hubo: Hydrator.ssr." + componentName,
            );
        }
    }
};

const doSsrAsync = (elements: Array<HTMLElement>, store: TStore) => {
    return new Promise<void>(resolve => {
        doSsr(elements, store).then(resolve);
    });
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export { SSR, SSRModal };
